import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import ImageUpload from '../components/ImageUpload';
import { stripeService } from '../services/stripeService';
import { carsAPI } from '../api';
import { CheckCircle, ArrowRight, ArrowLeft, Loader2, CreditCard, Car, Upload } from 'lucide-react';
import { isMobilitier } from '../lib/roles';

const MobilitierOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Stripe Connect
  const [stripeStatus, setStripeStatus] = useState({ connected: false });
  
  // Step 2: Car Details
  const [carData, setCarData] = useState({
    name: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    type: 'SEDAN',
    transmission: 'Automática',
    capacity: 5,
    price_per_day: '',
    location: '',
    description: '',
    features: ['Aire acondicionado'],
    mileage_limit: 200,
    fuel_policy: 'full-to-full',
    delivery_available: false,
    delivery_fee: 0,
    images: []
  });

  useEffect(() => {
    if (!isMobilitier(user)) {
      navigate('/');
      return;
    }
    checkStripeStatus();
  }, [user, navigate]);

  const checkStripeStatus = async () => {
    try {
      const status = await stripeService.getConnectStatus();
      setStripeStatus(status);
      if (status.connected && status.charges_enabled) {
        setStep(2); // Skip to car setup if already connected
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    }
  };

  const handleStripeConnect = async () => {
    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/mobilitier/onboarding?step=2`;
      const refreshUrl = `${window.location.origin}/mobilitier/onboarding?step=1`;
      
      const result = await stripeService.createConnectAccount(returnUrl, refreshUrl);
      
      // Redirect to Stripe onboarding
      window.location.href = result.onboarding_url;
    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast({
        title: 'Error',
        description: 'No se pudo conectar con Stripe. Intenta nuevamente.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const handleCarSubmit = async () => {
    if (!carData.name || !carData.price_per_day || carData.images.length < 4) {
      toast({
        title: 'Error',
        description: 'Completa todos los campos y sube al menos 4 fotos',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await carsAPI.create({
        ...carData,
        price_per_day: parseFloat(carData.price_per_day)
      });

      toast({
        title: '¡Auto publicado!',
        description: 'Tu auto ya está disponible para renta'
      });
      
      navigate('/mobilitier/dashboard');
    } catch (error) {
      console.error('Error creating car:', error);
      toast({
        title: 'Error',
        description: 'No se pudo publicar el auto',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center gap-4">
        {[1, 2].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              step >= s ? 'border-[#000000] bg-[#000000] text-white' : 'border-gray-300 text-gray-400'
            } font-semibold`}>
              {step > s ? <CheckCircle className="w-6 h-6" /> : s}
            </div>
            {s < 2 && <div className={`w-16 h-1 ${step > s ? 'bg-[#000000]' : 'bg-gray-300'}`} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Bienvenido a Mobility Mobilitier
          </h1>
          <p className="text-lg text-gray-600">
            Configura tu cuenta en 2 sencillos pasos y comienza a ganar
          </p>
        </div>

        {renderStepIndicator()}

        {/* Step 1: Stripe Connect */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-6 h-6" />
                Paso 1: Conecta tu cuenta bancaria
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-3">¿Por qué conectar Stripe?</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Recibe pagos directamente en tu cuenta bancaria</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Procesamiento seguro y encriptado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Verificación de identidad en minutos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Sin costos de configuración</span>
                  </li>
                </ul>
              </div>

              {stripeStatus.connected ? (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-green-600 mb-2">
                    ¡Stripe conectado exitosamente!
                  </p>
                  <Badge className="bg-green-600">Verificado</Badge>
                  <Button
                    className="mt-6 w-full bg-[#000000]"
                    onClick={() => setStep(2)}
                  >
                    Continuar al siguiente paso
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full bg-[#000000] hover:bg-ink-700 py-6 text-lg"
                  onClick={handleStripeConnect}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    <>
                      Conectar con Stripe
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Add Car */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-6 h-6" />
                Paso 2: Publica tu auto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="brand">Marca *</Label>
                  <Input
                    id="brand"
                    value={carData.brand}
                    onChange={(e) => setCarData({ ...carData, brand: e.target.value })}
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <Label htmlFor="model">Modelo *</Label>
                  <Input
                    id="model"
                    value={carData.model}
                    onChange={(e) => setCarData({ ...carData, model: e.target.value })}
                    placeholder="RAV4"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="year">Año *</Label>
                  <Input
                    id="year"
                    type="number"
                    value={carData.year}
                    onChange={(e) => setCarData({ ...carData, year: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <Select value={carData.type} onValueChange={(v) => setCarData({ ...carData, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEDAN">Sedán</SelectItem>
                      <SelectItem value="SUV">SUV</SelectItem>
                      <SelectItem value="PICKUP">Pickup</SelectItem>
                      <SelectItem value="JEEP">Jeep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="capacity">Capacidad</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={carData.capacity}
                    onChange={(e) => setCarData({ ...carData, capacity: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="name">Nombre del anuncio *</Label>
                <Input
                  id="name"
                  value={carData.name}
                  onChange={(e) => setCarData({ ...carData, name: e.target.value })}
                  placeholder="Toyota RAV4 2024"
                />
              </div>

              <div>
                <Label htmlFor="location">Ubicación *</Label>
                <Input
                  id="location"
                  value={carData.location}
                  onChange={(e) => setCarData({ ...carData, location: e.target.value })}
                  placeholder="Ciudad de México, CDMX"
                />
              </div>

              <div>
                <Label htmlFor="price">Precio por día (MXN) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={carData.price_per_day}
                  onChange={(e) => setCarData({ ...carData, price_per_day: e.target.value })}
                  placeholder="600"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Ganas el 80%: ${carData.price_per_day ? Math.round(parseFloat(carData.price_per_day) * 0.8) : 0} MXN/día
                </p>
              </div>

              <div>
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  value={carData.description}
                  onChange={(e) => setCarData({ ...carData, description: e.target.value })}
                  placeholder="Describe tu auto, características especiales, estado..."
                  rows={4}
                />
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Fotos del Auto * (Mínimo 4, Máximo 6)
                </Label>
                <ImageUpload
                  maxFiles={6}
                  onUploadComplete={(imgs) => setCarData({ ...carData, images: imgs })}
                  folder="cars"
                  existingImages={carData.images}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
                <Button
                  onClick={handleCarSubmit}
                  disabled={loading || carData.images.length < 4}
                  className="flex-1 bg-[#bcef3c] hover:bg-[#a8d92e] text-gray-900"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Publicando...
                    </>
                  ) : (
                    'Publicar Auto'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MobilitierOnboarding;
